import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";

import { Avatar, getInitials, tintForId } from "./Avatar";
import { noAutofill } from "@/lib/noAutofill";

export interface PickablePlayer {
  id: number;
  username: string | null;
  first_name: string | null;
  last_name?: string | null;
  photo_url?: string | null;
  rating?: number | null;
}

interface Props {
  /**
   * Prefetched list. In client-mode (no `searchFn`) this is the full
   * searchable set. In server-mode `players` is optional and used only to
   * resolve the display data for `value` before the user has interacted
   * with the picker (rare — typical flow is value=null → pick → remember).
   */
  players?: PickablePlayer[];
  value: number | null;
  onChange: (id: number | null) => void;
  excludeIds?: number[];
  placeholder?: string;
  /** Max rendered rows in the dropdown before scroll kicks in. */
  maxVisible?: number;
  /**
   * When provided, the picker switches to server-side search: the query
   * is debounced ~250 ms, then `searchFn(q)` is called (with q="" to get
   * a sensible "suggested" list). Results from the most recent query win
   * via a sequence counter — out-of-order responses are dropped.
   *
   * Use this when the pool of candidates is too large to ship to the
   * client (e.g. 1000+ registered players) so the user can find anyone,
   * not just someone in the top-N that was preloaded.
   */
  searchFn?: (query: string) => Promise<PickablePlayer[]>;
}

function useDebouncedSearch(
  query: string,
  searchFn: Props["searchFn"],
  delay = 250,
) {
  const [results, setResults] = useState<PickablePlayer[]>([]);
  // Start in loading state when server-search is active so the first render
  // shows "Ищу…" for the ~250 ms debounce window instead of the misleading
  // "Никого не нашли" placeholder.
  const [loading, setLoading] = useState<boolean>(() => !!searchFn);
  // Sequence counter — the latest fetch wins, earlier slow responses are
  // dropped. Prevents a stale "ан" result from overwriting a fresh "иван".
  const seqRef = useRef(0);
  // Stable ref to the caller's searchFn so parents don't need useCallback.
  const fnRef = useRef(searchFn);
  useEffect(() => { fnRef.current = searchFn; }, [searchFn]);

  useEffect(() => {
    if (!searchFn) return;
    const q = query.trim();
    const handle = window.setTimeout(() => {
      const mySeq = ++seqRef.current;
      setLoading(true);
      fnRef.current!(q)
        .then((res) => {
          if (mySeq === seqRef.current) setResults(res);
        })
        .catch(() => { /* swallow — list just stays as-is */ })
        .finally(() => {
          if (mySeq === seqRef.current) setLoading(false);
        });
    }, delay);
    return () => window.clearTimeout(handle);
    // `searchFn` is pulled via ref, so omit it from deps to avoid refetching
    // on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, delay]);

  return { results, loading };
}

function fullName(u: PickablePlayer): string {
  return (
    [u.first_name, u.last_name].filter(Boolean).join(" ") ||
    u.username ||
    `игрок #${u.id}`
  );
}

function matches(u: PickablePlayer, q: string): boolean {
  if (!q) return true;
  const needle = q.toLowerCase();
  const hay = [u.first_name, u.last_name, u.username]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
}

/**
 * Consistent player selector used anywhere we ask the user to pick a
 * teammate / opponent. Searchable list with avatars, rating on the right;
 * collapses into a compact "selected" chip once someone is picked.
 */
export function PlayerPicker({
  players,
  value,
  onChange,
  excludeIds,
  placeholder = "Поиск по имени или @",
  maxVisible = 6,
  searchFn,
}: Props) {
  const [query, setQuery] = useState("");
  // Remember who the user most-recently picked so the selected-chip can
  // render even when the row has scrolled out of the search results.
  const [lastPicked, setLastPicked] = useState<PickablePlayer | null>(null);

  const excludeSet = useMemo(
    () => new Set(excludeIds ?? []),
    [excludeIds],
  );

  const { results: serverResults, loading: serverLoading } = useDebouncedSearch(
    query,
    searchFn,
  );

  const filtered = useMemo(() => {
    if (searchFn) {
      // Server already matched the query; we just drop excluded ids.
      return serverResults.filter((u) => !excludeSet.has(u.id));
    }
    return (players ?? []).filter(
      (u) => !excludeSet.has(u.id) && matches(u, query.trim()),
    );
  }, [searchFn, serverResults, players, excludeSet, query]);

  const selected = useMemo<PickablePlayer | null>(() => {
    if (value == null) return null;
    if (lastPicked && lastPicked.id === value) return lastPicked;
    return players?.find((u) => u.id === value) ?? null;
  }, [value, lastPicked, players]);

  const pick = (u: PickablePlayer) => {
    setLastPicked(u);
    setQuery("");
    onChange(u.id);
  };

  const clear = () => {
    setLastPicked(null);
    onChange(null);
  };

  if (selected) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 12px",
          background: "var(--bg-section)",
          border: "1px solid var(--separator)",
          borderRadius: "var(--r-md)",
        }}
      >
        <Avatar
          initials={getInitials(selected.first_name, selected.last_name, selected.username)}
          size={40}
          tint={tintForId(selected.id)}
          src={selected.photo_url}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="body"
            style={{
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fullName(selected)}
          </div>
          <div className="caption" style={{ color: "var(--fg3)" }}>
            {selected.username ? `@${selected.username}` : "—"}
            {selected.rating != null && (
              <>
                {" "}· <span style={{ fontVariantNumeric: "tabular-nums" }}>{selected.rating}</span>
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={clear}
          aria-label="Сменить игрока"
          style={{
            background: "transparent",
            border: 0,
            padding: 6,
            color: "var(--fg3)",
            cursor: "pointer",
            display: "inline-flex",
          }}
        >
          <X size={18} />
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        border: "1px solid var(--separator)",
        borderRadius: "var(--r-md)",
        background: "var(--bg-section)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          borderBottom:
            filtered.length > 0 ? "1px solid var(--separator)" : undefined,
        }}
      >
        <Search size={16} color="var(--fg3)" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          {...noAutofill}
          style={{
            flex: 1,
            minWidth: 0,
            border: 0,
            outline: 0,
            background: "transparent",
            color: "var(--fg1)",
            fontSize: 15,
            fontFamily: "var(--font-body)",
          }}
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Очистить"
            style={{
              background: "transparent",
              border: 0,
              padding: 2,
              color: "var(--fg3)",
              cursor: "pointer",
              display: "inline-flex",
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div
          style={{
            padding: "16px 14px",
            color: "var(--fg2)",
            fontSize: 13,
            textAlign: "center",
          }}
        >
          {searchFn && serverLoading ? "Ищу…" : "Никого не нашли"}
        </div>
      ) : (
        <div
          style={{
            maxHeight: maxVisible * 56,
            overflowY: "auto",
          }}
        >
          {filtered.map((u, i) => (
            <button
              key={u.id}
              type="button"
              onClick={() => pick(u)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                width: "100%",
                background: "transparent",
                border: 0,
                borderTop: i === 0 ? 0 : "1px solid var(--separator)",
                padding: "10px 12px",
                cursor: "pointer",
                textAlign: "left",
                color: "var(--fg1)",
              }}
            >
              <Avatar
                initials={getInitials(u.first_name, u.last_name, u.username)}
                size={36}
                tint={tintForId(u.id)}
                src={u.photo_url}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="body"
                  style={{
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {fullName(u)}
                </div>
                {u.username && (
                  <div className="caption" style={{ color: "var(--fg3)" }}>
                    @{u.username}
                  </div>
                )}
              </div>
              {u.rating != null && (
                <div
                  className="caption"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 700,
                    color: "var(--brand-orange)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {u.rating}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
